import axios, { type InternalAxiosRequestConfig } from 'axios';

const BACKEND_BASE_URL =
  (import.meta.env['VITE_BACKEND_BASE_URL'] as string | undefined) ?? 'http://localhost:3000';
const MIDDLEWARE_BASE_URL =
  (import.meta.env['VITE_MIDDLEWARE_BASE_URL'] as string | undefined) ?? 'http://localhost:3100';

/** Backend — login (OTP request/verify) and token refresh only. */
export const backendClient = axios.create({
  baseURL: BACKEND_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/** Middleware — all Device Plane data (/admin-ops/*), called directly from the browser. */
export const middlewareClient = axios.create({
  baseURL: MIDDLEWARE_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

function attachToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
}
backendClient.interceptors.request.use(attachToken);
middlewareClient.interceptors.request.use(attachToken);

let isRefreshing = false;

// On 401 from middleware calls, refresh via the backend (refresh tokens are
// backend-owned) then retry against the middleware.
middlewareClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error.response?.status as number | undefined;
    if (status === 401 && !isRefreshing) {
      isRefreshing = true;
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const { data } = await backendClient.post<{ accessToken: string }>('/auth/refresh', {
            refreshToken,
          });
          localStorage.setItem('accessToken', data.accessToken);
          isRefreshing = false;
          error.config.headers['Authorization'] = `Bearer ${data.accessToken}`;
          return middlewareClient.request(error.config);
        } catch {
          // refresh failed — fall through to redirect
        }
      }
      isRefreshing = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);
